import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Activity } from "lucide-react";
import { useLivePrices } from "@/hooks/useLivePrices";

const TOP_SYMBOLS = ["BTC", "ETH", "SOL", "BNB", "XRP", "ADA", "DOGE", "AVAX"];

export const LiveCryptoPrices = () => {
  const { prices, isLoading, lastUpdated } = useLivePrices(TOP_SYMBOLS);

  const displayCoins = TOP_SYMBOLS
    .map(sym => {
      const data = prices.get(sym);
      if (!data) return null;
      return { symbol: sym, price: data.price, change24h: data.change24h, marketCap: data.marketCap || 0 };
    })
    .filter(Boolean)
    .slice(0, 6) as { symbol: string; price: number; change24h: number; marketCap: number }[];

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: price < 10 ? 2 : 0,
      maximumFractionDigits: price < 10 ? 2 : 0,
    }).format(price);
  };

  const formatMarketCap = (cap: number) => {
    if (cap >= 1_000_000_000) return `$${(cap / 1_000_000_000).toFixed(1)}B`;
    if (cap >= 1_000_000) return `$${(cap / 1_000_000).toFixed(1)}M`;
    return `$${cap.toLocaleString()}`;
  };

  return (
    <Card className="p-6 gradient-card border-primary/20">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-primary animate-pulse" />
          <h3 className="text-lg font-bold text-foreground">Live Prices (AUD)</h3>
        </div>
        <Badge variant="outline" className="text-xs border-primary/30">
          {isLoading ? 'Loading...' : lastUpdated
            ? `Updated ${lastUpdated.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}`
            : 'No data'}
        </Badge>
      </div>

      <div className="space-y-3">
        {isLoading && displayCoins.length === 0 ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-background/50 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-muted" />
                <div className="space-y-1">
                  <div className="h-4 w-16 bg-muted rounded" />
                  <div className="h-3 w-12 bg-muted rounded" />
                </div>
              </div>
              <div className="space-y-1 text-right">
                <div className="h-4 w-20 bg-muted rounded" />
                <div className="h-3 w-12 bg-muted rounded" />
              </div>
            </div>
          ))
        ) : displayCoins.map((coin) => (
          <div
            key={coin.symbol}
            className="flex items-center justify-between p-3 rounded-lg bg-background/50 hover:bg-background/80 transition-all group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center font-bold text-primary group-hover:scale-110 transition-transform">
                {coin.symbol.slice(0, 2)}
              </div>
              <div>
                <p className="font-bold text-foreground">{coin.symbol}</p>
                <p className="text-xs text-muted-foreground">{formatMarketCap(coin.marketCap)} MCap</p>
              </div>
            </div>

            <div className="text-right">
              <p className="font-mono font-bold text-foreground">{formatPrice(coin.price)}</p>
              <div className="flex items-center justify-end gap-1">
                {coin.change24h >= 0 ? (
                  <>
                    <TrendingUp className="w-3 h-3 text-success" />
                    <span className="text-xs font-mono text-success">+{coin.change24h.toFixed(2)}%</span>
                  </>
                ) : (
                  <>
                    <TrendingDown className="w-3 h-3 text-destructive" />
                    <span className="text-xs font-mono text-destructive">{coin.change24h.toFixed(2)}%</span>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-muted-foreground mt-4 text-center">
        Prices update every 60 seconds. Trade on{" "}
        <a href="https://www.coinspot.com.au?affiliate=J2WT2J" className="text-primary hover:underline">CoinSpot</a> or{" "}
        <a href="https://trade.swyftx.com/register/?ref=R6YKMQRQC9CBXAUN" className="text-primary hover:underline">Swyftx</a>
      </p>
    </Card>
  );
};
